import React, { useState } from 'react';
import { cn } from '../../utils/cn';
import Icon from '../AppIcon';

/**
 * ContentFormatter Components
 * Enhanced content formatting for better scannability and readability
 */

// ===== CONTENT CONTAINERS =====

/**
 * ReadableContent Component
 * Wraps content with optimal reading width and spacing
 */
export const ReadableContent = ({ 
  children, 
  width = 'default',
  className,
  ...props 
}) => {
  const widthClasses = {
    narrow: 'content-readable-narrow',
    default: 'content-readable',
    wide: 'content-readable-wide'
  };

  return (
    <div 
      className={cn(widthClasses[width], 'text-formatted', className)} 
      {...props}
    >
      {children}
    </div>
  );
};

/**
 * LeadParagraph Component
 * Emphasized introductory paragraph
 */
export const LeadParagraph = ({ 
  children, 
  size = 'default',
  className,
  ...props 
}) => {
  const sizeClasses = {
    default: 'text-lead',
    large: 'text-lead-large'
  };

  return (
    <p className={cn(sizeClasses[size], className)} {...props}>
      {children}
    </p>
  );
};

// ===== VISUAL BREAKS =====

/**
 * SectionDivider Component
 * Visual separator between content sections
 */
export const SectionDivider = ({ 
  thickness = 'default',
  className,
  ...props 
}) => {
  const thicknessClasses = {
    default: 'section-divider',
    thick: 'section-divider-thick'
  };

  return (
    <hr className={cn(thicknessClasses[thickness], className)} {...props} />
  );
};

/**
 * ContentSeparator Component
 * Text-based content separator
 */
export const ContentSeparator = ({ 
  children,
  className,
  ...props 
}) => {
  return (
    <div className={cn('content-separator', className)} {...props}>
      {children}
    </div>
  );
};

// ===== LISTS =====

/**
 * EnhancedList Component
 * Styled list with custom bullets
 */
export const EnhancedList = ({ 
  items = [],
  children,
  className,
  ...props 
}) => {
  return (
    <ul className={cn('list-enhanced', className)} {...props}>
      {children || items.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ul>
  );
};

/**
 * NumberedList Component
 * Styled numbered list
 */
export const NumberedList = ({ 
  items = [],
  children,
  className,
  ...props 
}) => {
  return (
    <ol className={cn('list-numbered', className)} {...props}>
      {children || items.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ol>
  );
};

// ===== HIGHLIGHTED CONTENT =====

/**
 * Callout Component
 * Highlighted content box with different variants
 */
export const Callout = ({ 
  children,
  variant = 'default',
  title,
  icon,
  className,
  ...props 
}) => {
  const variantClasses = {
    default: 'callout',
    info: 'callout-info',
    success: 'callout-success',
    warning: 'callout-warning',
    error: 'callout-error'
  };

  return (
    <div className={cn(variantClasses[variant], className)} {...props}>
      {title && (
        <div className="flex items-center mb-3">
          {icon && (
            <Icon 
              name={icon} 
              size={20} 
              className="mr-2" 
              color="currentColor"
            />
          )}
          <h4 className="font-semibold text-sm uppercase tracking-wide">
            {title}
          </h4>
        </div>
      )}
      <div className="text-sm leading-relaxed">
        {children}
      </div>
    </div>
  );
};

/**
 * Quote Component
 * Styled quote block
 */
export const Quote = ({ 
  children,
  author,
  source,
  className,
  ...props 
}) => {
  return (
    <blockquote className={cn('quote', className)} {...props}>
      <div className="quote-text">
        {children}
      </div>
      {(author || source) && (
        <div className="quote-author">
          {author && <span>{author}</span>}
          {author && source && <span> • </span>}
          {source && <span>{source}</span>}
        </div>
      )}
    </blockquote>
  );
};

// ===== CONTENT BLOCKS =====

/**
 * FeatureBlock Component
 * Highlighted feature or benefit block
 */
export const FeatureBlock = ({ 
  title,
  children,
  icon,
  iconColor = 'primary',
  className,
  ...props 
}) => {
  const iconColorClasses = {
    primary: 'bg-primary text-primary-foreground',
    secondary: 'bg-secondary text-secondary-foreground',
    accent: 'bg-accent text-accent-foreground',
    success: 'bg-success text-success-foreground',
    warning: 'bg-warning text-warning-foreground',
    error: 'bg-error text-error-foreground'
  };

  return (
    <div className={cn('feature-block', className)} {...props}>
      <div className="feature-block-header">
        {icon && (
          <div className={cn('feature-block-icon', iconColorClasses[iconColor])}>
            <Icon name={icon} size={20} />
          </div>
        )}
        <h3 className="feature-block-title">{title}</h3>
      </div>
      <div className="feature-block-content">
        {children}
      </div>
    </div>
  );
};

/**
 * StatBlock Component
 * Statistics and metrics display
 */
export const StatBlock = ({ 
  number,
  label,
  description,
  trend,
  className,
  ...props 
}) => {
  return (
    <div className={cn('stat-block', className)} {...props}>
      <div className="stat-number">{number}</div>
      <div className="stat-label">{label}</div>
      {description && (
        <div className="text-xs text-text-tertiary mt-2">
          {description}
        </div>
      )}
      {trend && (
        <div className={cn(
          'text-xs mt-2 font-medium',
          trend > 0 ? 'text-success' : 'text-error'
        )}>
          {trend > 0 ? '↗' : '↘'} {Math.abs(trend)}%
        </div>
      )}
    </div>
  );
};

// ===== PROGRESSIVE DISCLOSURE =====

/**
 * ExpandableContent Component
 * Collapsible content section
 */
export const ExpandableContent = ({ 
  title,
  children,
  defaultExpanded = false,
  className,
  ...props 
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <div className={cn('expandable-content', isExpanded && 'expanded', className)} {...props}>
      <button
        className="expandable-header"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
        aria-controls={`expandable-${title}`}
      >
        <h3 className="expandable-title">{title}</h3>
        <Icon 
          name="ChevronDown" 
          size={20} 
          className="expandable-icon"
        />
      </button>
      {isExpanded && (
        <div 
          id={`expandable-${title}`}
          className="expandable-body"
        >
          {children}
        </div>
      )}
    </div>
  );
};

// ===== CONTENT SECTIONS =====

/**
 * SectionHeader Component
 * Standardized section headers
 */
export const SectionHeader = ({ 
  title,
  subtitle,
  centered = true,
  className,
  ...props 
}) => {
  return (
    <div className={cn('section-header', centered && 'text-center', className)} {...props}>
      <h2 className="section-title">{title}</h2>
      {subtitle && (
        <p className="section-subtitle">{subtitle}</p>
      )}
    </div>
  );
};

/**
 * ContentGrid Component
 * Responsive content grid
 */
export const ContentGrid = ({ 
  children,
  columns = 'auto',
  gap = 'default',
  className,
  ...props 
}) => {
  const gapClasses = {
    sm: 'gap-4',
    default: 'gap-6',
    lg: 'gap-8'
  };

  return (
    <div 
      className={cn(
        'content-grid',
        gapClasses[gap],
        className
      )} 
      style={{
        gridTemplateColumns: typeof columns === 'number' 
          ? `repeat(${columns}, 1fr)` 
          : columns
      }}
      {...props}
    >
      {children}
    </div>
  );
};

/**
 * ContentGridItem Component
 * Individual grid item
 */
export const ContentGridItem = ({ 
  children,
  className,
  ...props 
}) => {
  return (
    <div className={cn('content-grid-item', className)} {...props}>
      {children}
    </div>
  );
};

// ===== COMPOSITE COMPONENTS =====

/**
 * ContentSection Component
 * Complete content section with header and content
 */
export const ContentSection = ({ 
  title,
  subtitle,
  children,
  className,
  ...props 
}) => {
  return (
    <section className={cn('py-8', className)} {...props}>
      <SectionHeader title={title} subtitle={subtitle} />
      <ReadableContent>
        {children}
      </ReadableContent>
    </section>
  );
};

/**
 * KeyPoints Component
 * List of key points with icons
 */
export const KeyPoints = ({ 
  points = [],
  icon = 'Check',
  className,
  ...props 
}) => {
  return (
    <EnhancedList className={className} {...props}>
      {points.map((point, index) => (
        <li key={index} className="flex items-start">
          <Icon 
            name={icon} 
            size={16} 
            className="mr-3 mt-1 text-success flex-shrink-0" 
          />
          <span>{point}</span>
        </li>
      ))}
    </EnhancedList>
  );
};

/**
 * ProcessSteps Component
 * Numbered process steps
 */
export const ProcessSteps = ({ 
  steps = [],
  className,
  ...props 
}) => {
  return (
    <NumberedList className={className} {...props}>
      {steps.map((step, index) => (
        <li key={index}>
          <div className="font-medium mb-2">{step.title}</div>
          {step.description && (
            <div className="text-sm text-text-secondary">
              {step.description}
            </div>
          )}
        </li>
      ))}
    </NumberedList>
  );
};

// Export all components
export default {
  ReadableContent,
  LeadParagraph,
  SectionDivider,
  ContentSeparator,
  EnhancedList,
  NumberedList,
  Callout,
  Quote,
  FeatureBlock,
  StatBlock,
  ExpandableContent,
  SectionHeader,
  ContentGrid,
  ContentGridItem,
  ContentSection,
  KeyPoints,
  ProcessSteps
};
